/**
 * Phase 17 — Admin Customer Follow-Up Email Scaffolding page.
 *
 * Route: /admin/partners/customer-follow-ups (admin only)
 *
 * PINNED USER RULE (Feb 2026):
 *   Phase 17 NEVER sends an email. This page only:
 *     • Generates preview rows from eligible partner-attributed
 *       opportunities.
 *     • Lets admin review subject + body, mark needs_review,
 *       approve_for_future_send (which DOES NOT send), suppress, void.
 *     • Append-only internal notes + audit log.
 *
 * Surfaces:
 *   1. Safety banner — "Email dispatch disabled — previews only".
 *   2. Generator card — booking_reference + category + event_type +
 *      partner_branding toggle.
 *   3. Filter bar — status / partner / category / severity / event_type.
 *   4. Preview list — clickable rows opening a side panel with full
 *      subject + body_text + status mutators + audit log.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, AlertTriangle, Loader2, ShieldAlert,
  Mail, Filter, Send, Eye, History, MessageSquare, X, Plus,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const STATUS_TONES = {
  draft_preview:           "bg-slate-100 text-slate-700",
  needs_review:            "bg-amber-100 text-amber-800",
  approved_for_future_send:"bg-emerald-100 text-emerald-800",
  suppressed:              "bg-rose-100 text-rose-800",
  void:                    "bg-slate-200 text-slate-500",
};

const SEVERITY_TONES = {
  high:    "bg-rose-100 text-rose-800",
  medium:  "bg-amber-100 text-amber-800",
  low:     "bg-slate-100 text-slate-700",
  routine: "bg-slate-100 text-slate-500",
};

const prettyStatus = (s) => (s || "").replaceAll("_", " ");

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU"); }
  catch { return iso; }
};


// ── Filter bar ──────────────────────────────────────────────────────────
function FilterBar({
  partners, statuses, events, severities, categories,
  partnerId, status, eventType, severity, category,
  onPartnerId, onStatus, onEventType, onSeverity, onCategory, onClear,
}) {
  const hasFilters =
    partnerId || status || eventType || severity || category;
  return (
    <section
      data-testid="ph17-filter-bar"
      className="rounded-2xl bg-white border border-slate-200 p-4 mt-4"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
          <Filter className="w-3.5 h-3.5 text-blue-700" /> Filters
        </div>
        {hasFilters && (
          <button
            data-testid="ph17-filters-clear"
            onClick={onClear}
            className="text-[11px] font-semibold text-slate-500 hover:text-slate-800"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-xs">
        <select
          data-testid="ph17-filter-partner"
          value={partnerId || ""}
          onChange={(e) => onPartnerId(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All partners</option>
          {partners.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
        <select
          data-testid="ph17-filter-status"
          value={status || ""}
          onChange={(e) => onStatus(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>{prettyStatus(s)}</option>
          ))}
        </select>
        <select
          data-testid="ph17-filter-event"
          value={eventType || ""}
          onChange={(e) => onEventType(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All event types</option>
          {events.map((e) => (
            <option key={e.slug} value={e.slug}>{e.label}</option>
          ))}
        </select>
        <select
          data-testid="ph17-filter-severity"
          value={severity || ""}
          onChange={(e) => onSeverity(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All severities</option>
          {severities.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          data-testid="ph17-filter-category"
          value={category || ""}
          onChange={(e) => onCategory(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.slug || c} value={c.slug || c}>
              {c.label || c}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
}


// ── Generator card ──────────────────────────────────────────────────────
function GeneratorCard({ events, onGenerate, busy }) {
  const [ref, setRef] = useState("");
  const [slug, setSlug] = useState("");
  const [evt, setEvt] = useState(events[0]?.slug || "");
  const [branding, setBranding] = useState(true);

  useEffect(() => {
    if (!evt && events.length) setEvt(events[0].slug);
  }, [events, evt]);

  const submit = () => {
    if (!ref.trim() || !slug.trim() || !evt) {
      toast.error("Booking reference, category slug and event type are required.");
      return;
    }
    onGenerate({
      booking_reference: ref.trim(),
      category_slug: slug.trim(),
      event_type: evt,
      allow_partner_branding: branding,
    });
  };

  return (
    <section
      data-testid="ph17-generator-card"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Plus className="w-4 h-4 text-blue-700" />
        <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
          Generate a preview
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
        <input
          data-testid="ph17-generate-booking"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder="Booking reference (e.g. SCWT-…)"
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white font-mono"
        />
        <input
          data-testid="ph17-generate-category"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="Category slug (e.g. ph_out_of_range)"
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white font-mono"
        />
        <select
          data-testid="ph17-generate-event"
          value={evt}
          onChange={(e) => setEvt(e.target.value)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          {events.map((e) => (
            <option key={e.slug} value={e.slug}>{e.label}</option>
          ))}
        </select>
        <label
          data-testid="ph17-generate-branding"
          className="flex items-center gap-2 text-xs text-slate-600 px-2 py-1.5 border border-slate-200 rounded-md bg-white cursor-pointer"
        >
          <input
            type="checkbox"
            checked={branding}
            onChange={(e) => setBranding(e.target.checked)}
          />
          Allow partner branding
        </label>
      </div>
      <div className="mt-3 flex items-center justify-end">
        <button
          onClick={submit}
          disabled={busy}
          data-testid="ph17-generate-submit"
          className="text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
        >
          <Send className="w-3.5 h-3.5" />
          {busy ? "Generating…" : "Generate preview"}
        </button>
      </div>
      <p className="mt-3 text-[10px] text-slate-400 italic">
        Generating a preview never sends an email. The row will appear in
        the queue as <strong>draft preview</strong>.
      </p>
    </section>
  );
}


// ── Preview side panel ──────────────────────────────────────────────────
function PreviewSidePanel({ preview, statuses, busy, onSave, onClose }) {
  const [status, setStatus] = useState(preview?.status || "");
  const [note, setNote] = useState("");

  useEffect(() => {
    setStatus(preview?.status || "");
    setNote("");
  }, [preview?.id, preview?.status]);

  if (!preview) return null;

  const dirty =
    status !== preview.status || note.trim().length > 0;

  return (
    <aside
      data-testid={`ph17-side-panel-${preview.id}`}
      className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl h-full overflow-y-auto p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-blue-700">
              Preview · {preview.event_type.replaceAll("_", " ")}
            </div>
            <h2 className="text-xl font-bold text-slate-900 mt-1">
              {preview.subject}
            </h2>
            <div className="text-xs text-slate-500 mt-1">
              Booking{" "}
              <Link
                to={`/admin/bookings/${preview.booking_reference}`}
                data-testid={`ph17-side-booking-link-${preview.id}`}
                className="font-mono text-blue-700 hover:underline"
              >
                {preview.booking_reference}
              </Link>
              {" · "}
              {preview.partner_business_name || (
                <em className="text-slate-400">(no partner)</em>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid={`ph17-side-close-${preview.id}`}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Dispatch-disabled banner inside the panel */}
        <div
          data-testid={`ph17-side-dispatch-banner-${preview.id}`}
          className="rounded-xl bg-rose-50 border border-rose-200 p-3 mb-4 text-xs text-rose-900 flex items-start gap-2"
        >
          <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Dispatch disabled.</strong>{" "}
            {preview.dispatch_disabled_reason ||
              "Phase 17 — customer follow-up emails are scaffolding only."}
          </div>
        </div>

        {/* Body */}
        <div
          data-testid={`ph17-side-body-${preview.id}`}
          className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 whitespace-pre-line"
        >
          {preview.body_text}
        </div>

        {/* Status + note editor */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">
              Status
            </div>
            <select
              data-testid={`ph17-side-status-${preview.id}`}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={`text-xs w-full border border-slate-200 rounded-md px-2 py-1.5 ${
                STATUS_TONES[status] || ""
              }`}
            >
              {statuses.map((s) => (
                <option key={s} value={s}>{prettyStatus(s)}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">
              Internal note (append-only)
            </div>
            <textarea
              data-testid={`ph17-side-note-${preview.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Admin-only note. No email is sent."
              rows={3}
              maxLength={1000}
              className="text-xs w-full border border-slate-200 rounded-md px-2 py-1.5 bg-white resize-y"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid={`ph17-side-cancel-${preview.id}`}
            className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 px-3 py-1.5 rounded-full border border-slate-200"
          >
            Close
          </button>
          <button
            onClick={() =>
              onSave(preview, {
                status: status !== preview.status ? status : null,
                internal_note: note.trim() ? note.trim() : null,
              })
            }
            disabled={!dirty || busy}
            data-testid={`ph17-side-save-${preview.id}`}
            className="text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 px-3 py-1.5 rounded-full"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>

        {/* Audit log + notes */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              <History className="w-3 h-3" /> Audit log
            </div>
            <ul className="space-y-1 text-[11px]">
              {(preview.audit_log || []).map((e, i) => (
                <li
                  key={i}
                  data-testid={`ph17-audit-entry-${preview.id}-${i}`}
                  className="rounded bg-slate-50 px-2 py-1"
                >
                  <span className="font-mono text-[10px] text-slate-500">
                    {fmtDateTime(e.changed_at)}
                  </span>
                  {" — "}
                  <span className="font-semibold">
                    {e.action}{" "}
                    {e.from_status && e.to_status && (
                      <>{prettyStatus(e.from_status)} → {prettyStatus(e.to_status)}</>
                    )}
                  </span>
                  {e.actor_email && (
                    <span className="ml-2 text-slate-500">by {e.actor_email}</span>
                  )}
                  {e.note && (
                    <div className="italic text-slate-600">“{e.note}”</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              <MessageSquare className="w-3 h-3" /> Internal notes
            </div>
            <ul className="space-y-1 text-[11px]">
              {(preview.internal_notes || []).map((n, i) => (
                <li
                  key={i}
                  data-testid={`ph17-note-${preview.id}-${i}`}
                  className="rounded bg-amber-50 border border-amber-100 px-2 py-1"
                >
                  <span className="font-mono text-[10px] text-slate-500">
                    {fmtDateTime(n.at)}
                  </span>
                  {n.by && (
                    <span className="ml-1 text-slate-500">· {n.by}</span>
                  )}
                  <div className="text-slate-700">{n.note}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </aside>
  );
}


// ── Preview queue ───────────────────────────────────────────────────────
function PreviewQueue({ data, onOpen }) {
  const rows = data?.rows || [];
  return (
    <section
      data-testid="ph17-queue"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-blue-700" />
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
            Customer Follow-Up Email Previews
          </h3>
        </div>
        <div className="text-xs text-slate-500">{rows.length}</div>
      </div>
      {rows.length === 0 ? (
        <div
          data-testid="ph17-queue-empty"
          className="text-xs text-slate-400 italic py-6 text-center"
        >
          No previews generated yet.
        </div>
      ) : (
        <div className="overflow-auto -mx-2">
          <table className="w-full text-xs" data-testid="ph17-queue-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left p-2">Booking</th>
                <th className="text-left p-2">Customer</th>
                <th className="text-left p-2">Partner</th>
                <th className="text-left p-2">Event</th>
                <th className="text-left p-2">Severity</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Created</th>
                <th className="text-right p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`ph17-row-${r.id}`}
                  className="border-t border-slate-100 hover:bg-blue-50/30 cursor-pointer"
                  onClick={() => onOpen(r)}
                >
                  <td className="p-2 font-mono text-blue-700">
                    {r.booking_reference}
                  </td>
                  <td className="p-2">{r.customer_name || "—"}</td>
                  <td className="p-2">
                    {r.partner_business_name || (
                      <span className="text-slate-400 italic">—</span>
                    )}
                  </td>
                  <td className="p-2">
                    <div className="font-semibold text-slate-700">
                      {r.event_type.replaceAll("_", " ")}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {r.category_slug}
                    </div>
                  </td>
                  <td className="p-2">
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        SEVERITY_TONES[r.severity] || ""
                      }`}
                    >
                      {r.severity}
                    </span>
                  </td>
                  <td className="p-2">
                    <span
                      data-testid={`ph17-row-status-${r.id}`}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        STATUS_TONES[r.status] || ""
                      }`}
                    >
                      {prettyStatus(r.status)}
                    </span>
                  </td>
                  <td className="p-2 text-[11px]">
                    {fmtDateTime(r.created_at)}
                  </td>
                  <td className="p-2 text-right">
                    <button
                      data-testid={`ph17-row-open-${r.id}`}
                      className="text-[11px] text-blue-700 hover:underline inline-flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" /> Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p
        data-testid="ph17-queue-disclaimer"
        className="mt-3 text-[10px] text-slate-400 italic"
      >
        {data?.disclaimer ||
          "Email dispatch disabled — previews only. No customer or partner emails are sent from this page."}
      </p>
    </section>
  );
}


// ── Page ────────────────────────────────────────────────────────────────
export default function AdminPWSCustomerFollowUps() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [catalog, setCatalog] = useState({
    event_types: [], statuses: [],
    dispatch_globally_enabled: false, dispatch_enabled_in_phase17: false,
    disclaimer: "",
  });
  const [list, setList] = useState({ rows: [], count: 0 });
  const [partnerId, setPartnerId] = useState(null);
  const [status, setStatusFilter] = useState(null);
  const [eventType, setEventType] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [category, setCategory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, l] = await Promise.all([
        api.get("/admin/pws-customer-follow-ups/catalog"),
        api.get("/admin/pws-customer-follow-ups", {
          params: {
            ...(status ? { status } : {}),
            ...(partnerId ? { partner_id: partnerId } : {}),
            ...(category ? { category } : {}),
            ...(severity ? { severity } : {}),
            ...(eventType ? { event_type: eventType } : {}),
            limit: 500,
          },
        }),
      ]);
      setCatalog(c.data);
      setList(l.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [partnerId, status, eventType, severity, category]);

  useEffect(() => { loadData(); }, [loadData]);

  const partners = useMemo(() => {
    const map = new Map();
    (list.rows || []).forEach((r) => {
      if (r.partner_id && !map.has(r.partner_id)) {
        map.set(r.partner_id, {
          id: r.partner_id,
          label: r.partner_business_name || r.partner_slug || r.partner_id,
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);

  const categories = useMemo(() => {
    const set = new Set();
    (list.rows || []).forEach((r) => {
      if (r.category_slug) set.add(r.category_slug);
    });
    return [...set].sort();
  }, [list]);

  const handleGenerate = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        "/admin/pws-customer-follow-ups/generate-preview", payload,
      );
      toast.success("Preview generated (no email sent).");
      setOpened(data?.row || null);
      await loadData();
    } catch (e) {
      toast.error(formatApiError(e) || "Generation failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (preview, payload) => {
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/admin/pws-customer-follow-ups/${encodeURIComponent(preview.id)}/status`,
        payload,
      );
      const updated = data?.row;
      setList((cur) => ({
        ...cur,
        rows: cur.rows.map((r) =>
          r.id === preview.id ? { ...r, ...updated } : r,
        ),
      }));
      setOpened(updated || preview);
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleClearFilters = () => {
    setPartnerId(null);
    setStatusFilter(null);
    setEventType(null);
    setSeverity(null);
    setCategory(null);
  };

  return (
    <PortalShell>
      <div
        data-testid="admin-pws-customer-follow-ups-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Link
          to="/admin/partners/network"
          data-testid="ph17-back-to-network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-blue-700">
              Aware Partner Network · Phase 17
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Customer Follow-Up Emails
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              Scaffolding for future customer follow-up emails — generate
              calm, factual previews; admin can review, approve for
              future send, suppress or void.{" "}
              <strong className="text-slate-700">
                Phase 17 NEVER sends an email. Dispatch is hard-disabled.
              </strong>
            </p>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            data-testid="ph17-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Safety banner */}
        <div
          data-testid="ph17-safety-banner"
          className="mt-5 rounded-xl bg-rose-50 border border-rose-200 p-4 flex items-start gap-3 text-sm text-rose-900"
        >
          <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Email dispatch disabled — previews only.</strong> No
            customer or partner emails are sent from this page. Templates
            use calm wording, include required Aware disclosures, treat
            BioCleanse as supplementary only, and never default to Soda
            Ash or Reverse Osmosis. Status changes are admin-only and
            audit-logged.
          </div>
        </div>

        <GeneratorCard
          events={catalog.event_types || []}
          busy={busy}
          onGenerate={handleGenerate}
        />

        <FilterBar
          partners={partners}
          statuses={catalog.statuses || []}
          events={catalog.event_types || []}
          severities={["high", "medium", "low", "routine"]}
          categories={categories}
          partnerId={partnerId}
          status={status}
          eventType={eventType}
          severity={severity}
          category={category}
          onPartnerId={setPartnerId}
          onStatus={setStatusFilter}
          onEventType={setEventType}
          onSeverity={setSeverity}
          onCategory={setCategory}
          onClear={handleClearFilters}
        />

        {loading ? (
          <div
            data-testid="ph17-loading"
            className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
          >
            <Loader2 className="w-5 h-5 animate-spin" /> Loading previews…
          </div>
        ) : error ? (
          <div
            data-testid="ph17-error"
            className="mt-8 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-800"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed to load
            </div>
            <div className="text-xs">{error}</div>
          </div>
        ) : (
          <PreviewQueue data={list} onOpen={setOpened} />
        )}

        <PreviewSidePanel
          preview={opened}
          statuses={catalog.statuses || []}
          busy={busy}
          onSave={handleSave}
          onClose={() => setOpened(null)}
        />
      </div>
    </PortalShell>
  );
}
